import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { settingTypes } from '@ipfabric/utils';
import type { VendorApiType } from '@ipfabric/utils/settings/vendorApi/names';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import React, { useMemo, type ReactNode } from 'react';
import Select, { type PropsValue } from 'react-select';
import type { VendorDialogType } from './constants';
import type { FormVendorValues } from './vendorTypes';

export const VENDOR_DIALOG_FORM_ID = 'vendor-dialog-form';

interface Props<T extends FormVendorValues> {
  dialogType: VendorDialogType;
  editName?: string;
  type: VendorApiType;
  setType: (type: VendorApiType) => void;
  formik: FormikProps<T>;
  children: React.ReactNode;
}

interface OptionType {
  value: VendorApiType;
  label: string;
}

export const VendorDialogContainer: <T extends FormVendorValues>(
  props: Props<T>,
) => ReactNode = React.memo((props) => {
  const msg = useLocale().discoverySettings.vendorsApi;
  const isVeloCloudEnabled = isFeatureFlagEnabled(
    'ENABLE_DISCOVERY_DEVICES_VELOCLOUD',
  );
  const isArubaNewCentralEnabled = isFeatureFlagEnabled(
    'ENABLE_DISCOVERY_DEVICES_ARUBANEWCENTRAL',
  );

  const typeOptions = useMemo(
    () =>
      Object.values(settingTypes.vendorApiNames)
        .filter(
          (vendor) =>
            msg.vendorTypes[vendor] !== undefined &&
            (vendor !== settingTypes.vendorApiNames.velocloud ||
              isVeloCloudEnabled) &&
            (vendor !== settingTypes.vendorApiNames.arubaNewCentral ||
              isArubaNewCentralEnabled),
        )
        .map((item) => {
          return { value: item, label: msg.vendorTypes[item] };
        })
        .sort((a, b) => a.label!.localeCompare(b.label!)),
    [isVeloCloudEnabled, isArubaNewCentralEnabled, msg.vendorTypes],
  );

  const handleTypeChange = (option: PropsValue<OptionType>): void => {
    if (option) {
      // Retyped due to bug in react-select types
      // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/32553
      props.setType((option as OptionType).value);
    }
  };

  const vendorTypeId = useHtmlId('vendorType');

  return (
    <form
      id={VENDOR_DIALOG_FORM_ID}
      onSubmit={props.formik.handleSubmit}
      autoComplete='off'
    >
      <Card noShadow className='overflow-hidden border border-default p-sm'>
        {props.dialogType === 'add' && (
          <div className='mb-2.5' data-testid='vendor-type-field'>
            <label className='block' htmlFor={vendorTypeId}>
              {msg.vendorDialog.type}
            </label>
            <Select
              id={vendorTypeId}
              classNamePrefix='react-select'
              isClearable={false}
              name='type'
              onChange={handleTypeChange}
              options={typeOptions}
              value={typeOptions.filter(
                (option) => option.value === props.type,
              )}
            />
          </div>
        )}

        {props.children}
      </Card>
    </form>
  );
});
